<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header bg-gradient-success mb-3">
          <h5 class="mb-0 text-white">
            <slot name="title">Listado</slot>
          </h5>
        </div>
        <div class="d-flex justify-content-between m-3">
          <div class="col-3">
            <div v-if="showSearch" class="mb-3">
              <input v-model="search" type="text" class="form-control input-icon" :placeholder="searchPlaceholder"/>
            </div>
          </div>
          <div class="col-3 d-flex align-items-center justify-content-end gap-2">
            <div v-if="!onlyList" class="view-toggle mb-3">
              <button class="btn btn-outline-success icon-toggle me-2" :class="{active: viewMode==='list'}"
                      @click="viewMode='list'">
                <i class="material-icons">view_list</i>
              </button>
              <button class="btn btn-outline-info icon-toggle" :class="{active: viewMode==='cards'}"
                      @click="viewMode='cards'">
                <i class="material-icons">view_module</i>
              </button>
            </div>
          </div>
          <div class="col-6 d-flex justify-content-end">
            <slot name="new"/>
          </div>
        </div>
        <div v-if="viewMode==='list'" class="table-responsive">
          <table class="table table-striped entity-table">
            <thead>
            <tr>
              <th v-for="col in columns" :key="col.key" :class="col.align ? `text-${col.align}` : ''">{{ col.label }}</th>
              <th v-if="hasActions" class="text-center">Acciones</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in paginatedItems" :key="item && item.id ? item.id : `item-${index}`">
              <td v-for="col in columns" :key="col.key" :class="col.align ? `text-${col.align}` : ''">
                <span v-if="col.formatter" v-html="col.formatter(item[col.key], item)"></span>
                <span v-else>{{ item[col.key] }}</span>
              </td>
              <td v-if="hasActions" class="text-center">
                <slot name="actions" :item="item"/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <!-- Card view -->
        <div v-else class="card-list p-3">
          <div v-for="(item, index) in paginatedItems" :key="item && item.id ? item.id : `item-${index}`" class="user-card-3d">
            <slot name="card" :item="item"/>
            <div class="user-card-actions">
              <slot name="actions" :item="item"/>
            </div>
          </div>
        </div>
        <!-- Pagination + PageSize Selector -->
        <div class="pagination-bar" v-if="totalPages > 0">
          <div class="pagination">
            <button
              type="button"
              class="pagination-btn nav-btn"
              :disabled="currentPage === 1"
              @click="goToPage(currentPage - 1)"
            >
              Anterior
            </button>
            <template v-for="(item, index) in paginationItems" :key="`page-${index}-${item}`">
              <span v-if="item === 'ellipsis'" class="pagination-ellipsis" aria-hidden="true">...</span>
              <button
                v-else
                type="button"
                class="pagination-btn page-num"
                :class="{ active: item === currentPage }"
                @click="goToPage(item)"
              >
                {{ item }}
              </button>
            </template>
            <button
              type="button"
              class="pagination-btn nav-btn"
              :disabled="currentPage === totalPages"
              @click="goToPage(currentPage + 1)"
            >
              Siguiente
            </button>
          </div>
          <span class="pagination-summary">Página {{ currentPage }} de {{ totalPages }}</span>
          <div class="page-size-select">
            <label for="pageSize" class="me-2">Mostrar</label>
            <select v-model="pageSize" id="pageSize" class="form-select">
              <option :value="10">10</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
            <span class="ms-2">por página</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EntityListDisplay",
  props: {
    items: {type: Array, required: true},
    columns: {type: Array, required: true}, // [{ key: 'firstName', label: 'Nombre' }, ...] o { key, label, formatter }
    showSearch: {type: Boolean, default: true},
    searchPlaceholder: {type: String, default: 'Buscar...'},
    hasActions: {type: Boolean, default: true},
    onlyList: {type: Boolean, default: false}
  },
  data() {
    return {
      search: "",
      viewMode: "list",
      currentPage: 1,
      pageSize: 10
    };
  },
  computed: {
    filteredItems() {
      // Filtrar items nulos o undefined
      const validItems = (this.items || []).filter(item => item != null);
      
      if (!this.search) return validItems;
      return validItems.filter(item => {
        return this.columns.some(col => {
          const value = item[col.key];
          return value != null && value.toString().toLowerCase().includes(this.search.toLowerCase());
        });
      });
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    totalPages() {
      const pages = Math.ceil(this.filteredItems.length / this.pageSize);
      return pages > 0 ? pages : 0;
    },
    /** Páginas visibles con elipsis para no desbordar cuando hay muchas páginas */
    paginationItems() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 0) return [];
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const delta = 1;
      const pageSet = new Set([1, total]);
      for (let p = current - delta; p <= current + delta; p++) {
        if (p >= 1 && p <= total) pageSet.add(p);
      }

      const sorted = [...pageSet].sort((a, b) => a - b);
      const items = [];
      let previous = 0;
      for (const page of sorted) {
        if (page - previous > 1) items.push('ellipsis');
        items.push(page);
        previous = page;
      }
      return items;
    }
  },
  watch: {
    items() {
      this.clampCurrentPage();
    },
    search() {
      this.currentPage = 1;
    },
    pageSize() {
      this.currentPage = 1;
    },
    filteredItems() {
      this.clampCurrentPage();
    }
  },
  methods: {
    clampCurrentPage() {
      if (this.totalPages > 0 && this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
      if (this.currentPage < 1) {
        this.currentPage = 1;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) this.currentPage = page;
    }
  }
};
</script>

<style scoped>
.card-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  padding: 1rem;
  justify-items: stretch;
}

.bg-gradient-success {
  background: linear-gradient(90deg, #3A9F1D 0%, #5fe939 100%);
}

.view-toggle {
  margin-bottom: 1rem;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.icon-toggle {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  padding: 0;
  border-radius: 50%;
  transition: background 0.2s;
  color: #3A9F1D;
  border: 2px solid #3A9F1D;
}

.icon-toggle.active {
  background: #3A9F1D;
  color: #fff;
  border: 2px solid #022126;
}

.card-header {
  border-radius: 12px 12px 0 0;
  padding: 1rem 1.5rem;
  margin-bottom: 0.5rem;
}

/* Card 3D Modern Style */
.user-card-3d {
  background: linear-gradient(135deg, #f8fff8 0%, #eafeea 100%);
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(60, 159, 29, 0.18), 0 1.5px 0.5px #5fe939 inset;
  padding: 1.7rem 1.5rem 1.2rem 1.5rem;
  margin-bottom: 0;
  transition: box-shadow 0.25s, transform 0.18s;
  position: relative;
  border: 1.5px solid #5fe93933;
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
}

.user-card-3d:hover {
  box-shadow: 0 16px 48px rgba(60, 159, 29, 0.28), 0 2px 1px #3A9F1D inset;
  transform: translateY(-4px) scale(1.04);
}

.user-info i {
  font-size: 1.1rem;
  margin-right: 0.5rem;
  color: #5fe939;
}

.user-card-actions {
  display: flex;
  gap: 0.7rem;
  justify-content: flex-end;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
  gap: 1rem;
  flex-wrap: wrap;
  max-width: 100%;
  padding: 0 0.5rem;
}

.pagination {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  flex-wrap: nowrap;
  max-width: 100%;
  overflow-x: auto;
  padding: 0.15rem 0;
}

.pagination-summary {
  font-size: 0.9rem;
  color: #3A9F1D;
  font-weight: 500;
  white-space: nowrap;
}

.pagination-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  padding: 0 0.15rem;
  color: #3A9F1D;
  font-weight: 700;
  user-select: none;
  flex-shrink: 0;
}

.page-size-select {
  display: flex;
  align-items: center;
  background: #f8fff8;
  border-radius: 8px;
  padding: 0.3rem 0.8rem;
  box-shadow: 0 2px 8px rgba(60, 159, 29, 0.08);
  font-size: 1rem;
  color: #3A9F1D;
  font-weight: 500;
}

.page-size-select label {
  font-weight: 500;
  color: #3A9F1D;
  margin-bottom: 0;
}

.page-size-select select {
  width: 80px;
  height: 40px;
}

.pagination-btn {
  background: #3A9F1D;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  font-weight: bold;
  flex-shrink: 0;
}

.pagination-btn.nav-btn {
  padding: 0.45rem 0.85rem;
}

.pagination-btn.page-num {
  min-width: 2.25rem;
  padding: 0.45rem 0.55rem;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background: #022126;
}

/* Estilos para alineación de tablas */
.entity-table {
  width: 100%;
  border-collapse: collapse;
}

.entity-table thead th {
  vertical-align: middle;
  text-align: left;
  padding: 0.75rem;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
}

.entity-table tbody td {
  vertical-align: middle;
  text-align: left;
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
}

.entity-table thead th.text-center,
.entity-table tbody td.text-center {
  text-align: center;
}

.entity-table thead th.text-end,
.entity-table tbody td.text-end {
  text-align: right;
}

.entity-table thead th.text-start,
.entity-table tbody td.text-start {
  text-align: left;
}

/* Asegurar que el contenido de las celdas esté alineado con los headers */
.entity-table tbody tr {
  height: auto;
}

.entity-table tbody td span {
  display: inline-block;
  vertical-align: middle;
}
</style>
